import { ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ISingleEventRankRanges } from 'routes/singleEvent/types';

import { ReactComponent as ArrowSvg } from 'assets/chevron.svg';

import { formatRMVWithIcon } from '../../utils/formatRMVWithIcon';

type MobileRankRangeItemProps = {
  label: string;
  value: string | ReactNode;
  variant?: 'default' | 'turquoise';
};

const MobileRankRangeItem = ({ label, value, variant = 'default' }: MobileRankRangeItemProps) => (
  <div className="tw-flex tw-w-[100%] tw-items-center tw-justify-between tw-rounded-[6px] tw-bg-[#112D68] tw-p-[8px]">
    <span className="tw-flex-1 tw-text-[16px] tw-font-semibold tw-text-[#4987FF]">{label}</span>
    <span
      className={twMerge(
        'tw-flex-1 tw-text-right tw-text-[16px] tw-font-semibold',
        variant === 'turquoise' ? 'tw-text-turquoise' : 'tw-text-white'
      )}
    >
      {value}
    </span>
  </div>
);

type RankRangesSectionProps = {
  rankRanges: ISingleEventRankRanges;
};

export const RankRangesSection = ({ rankRanges }: RankRangesSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="tw-mx-[20px] tw-max-w-[800px] tw-rounded-[16px] tw-bg-[#143478] tw-p-[24px] lg:tw-mx-auto">
      <div
        className="tw-flex tw-cursor-pointer tw-flex-row tw-items-center tw-justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className="tw-text-[24px] tw-font-bold tw-uppercase tw-leading-[32px] tw-text-turquoise">
          {rankRanges.title}
        </p>
        <ArrowSvg className={twMerge('[transform:rotate(180deg)]', isOpen && '[transform:rotate(0deg)]')} />
      </div>
      {isOpen && (
        <div className="tw-mt-[16px] tw-text-left tw-text-[16px] tw-font-medium tw-text-white">
          <p>{rankRanges.description}</p>

          <div className="tw-mt-[16px]">
            <div className="tw-flex tw-hidden tw-flex-row tw-items-center tw-text-[16px] tw-font-semibold tw-text-white lg:tw-flex">
              <div className="tw-flex-1 tw-text-center">Rank</div>
              <div className="tw-flex-1 tw-text-center">Prize Allocation</div>
              <div className="tw-flex-1 tw-text-center">Requirements</div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-[8px] lg:tw-gap-[0px]">
              {rankRanges.ranges.map((range, index) => (
                <div key={index}>
                  <div className="tw-mt-[8px] tw-flex tw-hidden tw-flex-row tw-items-center tw-rounded-[6px] tw-bg-[#0E2659] tw-py-4 tw-text-[18px] tw-font-semibold lg:tw-flex">
                    <div className="tw-flex-1 tw-text-center tw-text-white">{range.rank}</div>
                    <div className="tw-flex-1 tw-text-center tw-text-turquoise">
                      {formatRMVWithIcon(range.prizeAllocation)}
                    </div>
                    <div className="tw-flex-1 tw-text-center tw-text-turquoise">
                      {formatRMVWithIcon(range.requirements)}
                    </div>
                  </div>

                  <div className="tw-flex tw-flex-col tw-gap-[4px] tw-rounded-[6px] tw-bg-[#0E2659] tw-p-[6px] lg:tw-hidden">
                    <MobileRankRangeItem label="Rank" value={range.rank} />
                    <MobileRankRangeItem
                      label="Prize Allocation"
                      value={formatRMVWithIcon(range.prizeAllocation)}
                      variant="turquoise"
                    />
                    <MobileRankRangeItem
                      label="Min. Trading Volume Requirement"
                      value={formatRMVWithIcon(range.requirements)}
                      variant="turquoise"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
